export type SpreadsheetCell = string | number | boolean | Date | null;
export type SpreadsheetRow = SpreadsheetCell[];

export async function writeSpreadsheetBlob(rows: SpreadsheetRow[], sheetName: string) {
  const { default: writeXlsxFile } = await import("write-excel-file/browser");
  return writeXlsxFile(rows, { sheet: sheetName }).toBlob();
}

export async function readSpreadsheetRows(input: Blob) {
  const format = await detectImportFormat(input);
  if (format === "vcf") throw new Error("Ce fichier VCF doit être importé depuis les Contacts.");
  if (format === "csv") {
    return { sheetName: "CSV", rows: filterEmptyRows(parseCsvRows(await input.text())) };
  }
  const { default: readXlsxFile } = await import("read-excel-file/browser");
  const sheets = await readXlsxFile(input);
  const normalizedSheets = sheets.map((sheet) => ({
    name: sheet.sheet,
    rows: filterEmptyRows(sheet.data as SpreadsheetRow[]),
  }));
  const selectedSheet = normalizedSheets.find((sheet) => sheet.rows.length > 1) || normalizedSheets[0];

  return {
    sheetName: selectedSheet?.name || "",
    rows: selectedSheet?.rows || [],
  };
}

export function spreadsheetCellToString(value: SpreadsheetCell | undefined) {
  if (value instanceof Date) {
    return formatSpreadsheetDate(value);
  }
  return String(value ?? "").trim();
}

function filterEmptyRows(rows: SpreadsheetRow[]) {
  return rows.filter((row) => row.some((cell) => spreadsheetCellToString(cell)));
}

function formatSpreadsheetDate(value: Date) {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export type ImportColumn = { label: string; examples: string[]; target: string };
export type ImportField = { key: string; label: string; required?: boolean; requiredUnless?: string[] };

export function normalizeImportColumn(value: unknown) {
  return String(value ?? "").normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
}

export function prepareImportColumns(rows: SpreadsheetRow[], resolve: (cell: SpreadsheetCell) => string, fields: ImportField[]): ImportColumn[] {
  return rows[0].map((cell, index) => {
    const resolved = resolve(cell);
    const target = fields.find(field => field.key === resolved)?.key
      || fields.find(field => normalizeImportColumn(field.label) === normalizeImportColumn(cell))?.key
      || "";
    return {
      label: spreadsheetCellToString(cell) || `Colonne ${index + 1}`,
      examples: rows.slice(1, 4).map(row => spreadsheetCellToString(row[index])).filter(Boolean),
      target: fields.some(field => field.key === target) ? target : "",
    };
  });
}

export function validateImportMapping(mapping: string[], fields: ImportField[]) {
  const errors: string[] = [];
  for (const field of fields) {
    const count = mapping.filter(key => key === field.key).length;
    if (field.required && !count && !field.requiredUnless?.some(key => mapping.includes(key))) errors.push(`Champ requis manquant : ${field.label}.`);
    if (count > 1) errors.push(`Plusieurs colonnes correspondent à « ${field.label} ». Choisissez une seule colonne.`);
  }
  if (!mapping.some(Boolean)) errors.push("Choisissez au moins une colonne à importer.");
  if (mapping.some(key => key && !fields.some(field => field.key === key))) errors.push("Champ de destination inconnu.");
  return errors;
}

export async function detectImportFormat(input: Blob): Promise<"csv" | "xlsx" | "vcf"> {
  const signature = new Uint8Array(await input.slice(0, 4).arrayBuffer());
  if (signature[0] === 0x50 && signature[1] === 0x4b) return "xlsx";
  const prefix = await input.slice(0, 4096).text();
  if (/^\s*BEGIN:VCARD\s*$/im.test(prefix.replace(/^\uFEFF/, ""))) return "vcf";
  if (signature[0] === 0xd0 && signature[1] === 0xcf) throw new Error("Le format XLS ancien n’est pas pris en charge. Enregistrez le fichier en XLSX ou CSV.");
  if (prefix.includes("\0")) throw new Error("Format non reconnu. Choisissez un fichier CSV, XLSX ou VCF.");
  return "csv";
}

export function parseCsvRows(text: string): SpreadsheetRow[] {
  text = text.replace(/^\uFEFF/, "");
  // Count separators outside quotes on the first record (including quoted line breaks).
  const counts = new Map([[",", 0], [";", 0], ["\t", 0]]);
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    if (char === '"') {
      if (quoted && text[i + 1] === '"') { i++; continue; }
      quoted = !quoted;
    }
    if (!quoted && (char === "\n" || char === "\r")) break;
    if (!quoted && counts.has(char)) counts.set(char, counts.get(char)! + 1);
  }
  const delimiter = [...counts].sort((a, b) => b[1] - a[1])[0][0];
  const rows: SpreadsheetRow[] = [];
  let row: string[] = [], cell = "";
  quoted = false;
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    if (char === '"') {
      if (quoted && text[i + 1] === '"') { cell += '"'; i++; }
      else quoted = !quoted;
    } else if (!quoted && char === delimiter) { row.push(cell); cell = ""; }
    else if (!quoted && (char === "\n" || char === "\r")) {
      row.push(cell); rows.push(row); row = []; cell = "";
      if (char === "\r" && text[i + 1] === "\n") i++;
    } else cell += char;
  }
  if (quoted) throw new Error("Le CSV contient un champ entre guillemets non fermé.");
  if (cell || row.length) { row.push(cell); rows.push(row); }
  return rows;
}
